/*
 * FilePond 4.x.x
 * Licensed under MIT, https://opensource.org/licenses/MIT
 * Please visit https://pqina.nl/filepond for details.
 */
import React from 'react'
import './filepond.css'
import { create, supported, registerPlugin } from 'filepond'
import { SHOW_FEEDBACK, EnvironmentVariable, StaticEnvironmentFinder } from '@syncfab/machine'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'

// We need to be able to call the registerPlugin method directly so we can add plugins
// Do this once
const isSupported = supported()
registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize)

// returns file sources from the <File/> child objects
const getFilesFromChildren = children =>
    children
        ? React.Children.map(children, child => {
              const props = child.props

              // new mapping
              if (props.src) {
                  const options: any = {}
                  if (props.origin) {
                      options.type = props.origin
                  }
                  if (props.name) {
                      options.file = {
                          name: props.name,
                          size: props.size,
                          type: props.type,
                      }
                  }
                  if (props.metadata) {
                      options.metadata = props.metadata
                  }
                  return {
                      source: props.src,
                      options,
                  }
              }

              // deprecated mapping
              if (props.source && props.type) {
                  return {
                      source: props.source,
                      options: {
                          type: props.type,
                      },
                  }
              }

              return props.source
          })
        : []

// filtered methods
const filteredMethods = ['setOptions', 'on', 'off', 'onOnce', 'appendTo', 'insertAfter', 'insertBefore', 'isAttachedTo', 'replaceElement', 'restoreElement', 'destroy']

const defaultServer = (url, type, token, onload, onError, referenceId) => ({
    url,
    process: {
        url: !!type ? `/documents/${type}?referenceId=${referenceId}` : `/documents?referenceId=${referenceId}`,
        method: 'POST',
        withCredentials: false,
        headers: {
            authorization: `Bearer ${token}`,
        },
        onload: response => onload(JSON.parse(response)),
        onerror: error => onError(error),
    },
    load: null,
    restore: null,
    fetch: null,
    revert: null,
})

// The React <FilePond/> wrapper
// TODO Properties for this component need to be defined and typed
class FilePondComponent extends React.Component<any> {
    private _pond: any
    private _element: any

    constructor(props) {
        super(props)

        this.validateFileTypeAndExtension = this.validateFileTypeAndExtension.bind(this)
    }

    // Will setup FilePond instance when mounted
    componentDidMount() {
        // exit here if not supported
        if (!isSupported) {
            return
        }

        // TODO(JR): Abstract environment
        const server = defaultServer(
            StaticEnvironmentFinder(EnvironmentVariable.API_LAYER_URL),
            this.props.type || 'doc',
            this.props.authToken,
            this.props.onLoad,
            this.props.onError,
            this.props.referenceId
        )
        // Create our pond
        this._pond = create(this._element, {
            ...this.props,
            onprocessfile: (_error, _file) => {
                if (!!this.props.fileProcessed) {
                    this.props.fileProcessed(_file.file)
                }
                this._pond.removeFile(_file)
            },
            onwarning: (error) => {
                if (error.body === 'Max files' && !!this.props.handleWarning) {
                    this.props.handleWarning(`Only ${this.props.maxFiles} files may be uploaded at a time.`)
                }
            },
            onprocessfiles: () => {
                if (this.props.allFilesProcessed) {
                    this.props.allFilesProcessed()
                }
                if (!!this.props.clearAfterAllFilesProcessed) {
                    this._pond.removeFiles()
                }
            },
            // need to use 'any' type here because error object provided by filepond does not match their type def
            onerror: (error: any, file, _status) => {
                this.props.handleError(`${error.main} - ${file.filename}`)
            },
            labelButtonAbortItemProcessing: '',
            labelButtonRemoveItem: '',
            files: getFilesFromChildren(this.props.children),
            fileValidateTypeDetectType: this.validateFileTypeAndExtension,
        })

        this._pond.setOptions({
            server,
        })
        // Reference pond methods to FilePond component instance
        Object.keys(this._pond)
            .filter(key => !filteredMethods.includes(key))
            .forEach(key => {
                this[key] = this._pond[key]
            })
        !!this.props.onInitialized && this.props.onInitialized(this._pond)
    }

    // Will clean up FilePond instance when unmounted
    componentWillUnmount() {
        // exit when no pond defined
        if (!this._pond) {
            return
        }

        this._pond.destroy()
    }

    // Something changed
    componentDidUpdate(prevProps) {
        // exit when no pond defined
        if (!this._pond) {
            return
        }

        const options: any = Object.assign({}, this.props)

        // test if file list has changed
        const previousFiles = getFilesFromChildren(prevProps.children)
        const currentFiles = getFilesFromChildren(this.props.children)
        if (JSON.stringify(previousFiles) !== JSON.stringify(currentFiles)) {
            options.files = currentFiles
        }

        this._pond.setOptions(options)
    }

    private validateFileTypeAndExtension(file, type: string): Promise<string> {
        return new Promise((resolve, reject) => {
            // Do custom type detection here and return with promise
            if (!!type && this.props.acceptedFileTypes.find(fileType => fileType === type)) {
                resolve(type)
            } else {
                // Unrecognized mime type, looking for a file extension
                const uploadedFileExtension = file.name
                    .split('.')
                    .pop()
                    .toLowerCase()
                // Checking if the file extension is accepted
                const isAllowed = this.props.acceptedFileExtensions.find(fileType =>
                    fileType.split('.').pop() === uploadedFileExtension
                ) !== undefined
                if (isAllowed) {
                    // Resolve with our "false" mime type
                    resolve('allowed/extension')
                } else {
                    // Even the extension is not accepted, reject
                    reject('.' + uploadedFileExtension)
                }
            }
        })
    }

    // Renders basic element hook for FilePond to attach to
    render() {
        const { id, name, className, allowMultiple, required, captureMethod, acceptedFileTypes } = this.props

        return React.createElement(
            'div',
            { className: 'filepond--wrapper' },
            React.createElement('input', {
                type: 'file',
                name,
                id,
                accept: acceptedFileTypes,
                multiple: allowMultiple,
                required: required,
                className: className,
                capture: captureMethod,
                ref: element => (this._element = element),
            })
        )
    }
}

const mapStateToProps = ({ auth: { authToken } }) => {
    return {
        authToken,
    }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onError: () => dispatch(SHOW_FEEDBACK('document-upload-failed')),
    }
}

export const FilePondWrapper = connect(mapStateToProps, mapDispatchToProps)(FilePondComponent)

// <File/>, needs to be further extended with prop types
export class File extends React.Component {}
